import moment, { MomentInput } from 'moment';
import numeral from 'numeral';
import { FC, useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  useRowSelect,
  useSortBy,
  useTable,
  useGlobalFilter,
  useFilters,
  Row,
} from 'react-table';
import { matchSorter } from 'match-sorter';
import {
  deleteIcon,
  duplicate,
  edit,
  quickActionIcon,
  SvgEye,
  SvgTrash,
} from '../../../assets/svg/general';
import { statusColors } from '../../../constants';
import { CheckboxToggle, Toggle } from './toggle';
import { usePermissions } from '../../../hooks';
import { getOptionPrice } from '../../../helpers';

export interface RowAction {
  label: string;
  action: (row: any) => void;
}

interface ITable {
  data: any;
  columns: any;
  openPage?: any;
  tableFor: string;
  editMethod?: any;
  quickAction?: any;
  paginated?: boolean;
  showSearch?: boolean;
  setSelectedRows?: any;
  selectedRows?: any[];
  containerClassName?: string;
  deleteMethod?: (key: string) => void;
  deleteMultiple?: (key: string[]) => void;
  duplicateMethod?: (key: string) => void;
  getRowId?: (row: object, relativeIndex: number, parent?: any) => string;
  theadClassName?: string;
  rowActions?: RowAction[];
}

function fuzzyTextFilterFn(rows: Row[], columnIds: any, filterValue: any) {
  return matchSorter(rows, filterValue, {
    keys: [(row: Row) => row.values[columnIds]],
  });
}

fuzzyTextFilterFn.autoRemove = (val: any) => !val;

const StatusPill = ({ value }: { value: string }) => {
  const normalized = (typeof value === 'boolean' ? (value ? 'active' : 'inactive') : String(value ?? ''))?.toLowerCase().replace(/_/g, ' ');
  const isActive =
    normalized === 'active' ||
    normalized === 'available' ||
    normalized === 'open' ||
    normalized === 'opened';
  const isDanger =
    normalized === 'out of stock' ||
    normalized === 'canceled' ||
    normalized === 'inactive';

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs medium ${
        isActive
          ? 'bg-success-subtle text-success'
          : isDanger
            ? 'bg-red-50 text-red-500'
            : 'bg-medium text-text'
      }`}
    >
      {normalized?.charAt(0).toUpperCase() + normalized?.slice(1)}
    </span>
  );
};

export const Table: FC<ITable> = ({
  data,
  columns,
  tableFor,
  openPage,
  paginated,
  editMethod,
  quickAction,
  selectedRows,
  setSelectedRows,
  showSearch = true,
  containerClassName,
  getRowId,
  deleteMethod,
  deleteMultiple,
  duplicateMethod,
  theadClassName,
  rowActions,
}) => {
  const filterTypes = useMemo(
    () => ({
      fuzzyText: fuzzyTextFilterFn,
      text: (rows: Row[], columnIds: any, filterValue: any) => {
        return rows.filter((row) => {
          const rowValue = row.values[columnIds];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { selectedRowIds, globalFilter },
    selectedFlatRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      getRowId,
      initialState: {
        selectedRowIds: (selectedRows || []).reduce(
          (acc: Record<string, boolean>, id: string) => {
            acc[id] = true;
            return acc;
          },
          {}
        ),
      },
      filterTypes,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useRowSelect
  );

  const { isOwner } = usePermissions();

  const [value, setValue] = useState(globalFilter);

  const ActionDropdown = ({ actions, row }: any) => {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

    useEffect(() => {
      if (!open) return;
      const handler = (e: MouseEvent) => {
        if (
          buttonRef.current &&
          (buttonRef.current.contains(e.target as Node) ||
            (menuRef.current && menuRef.current.contains(e.target as Node)))
        )
          return;
        setOpen(false);
      };
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }, [open]);

    useEffect(() => {
      if (!open || !buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const top = rect.bottom + window.scrollY;
      const menuWidth = 192;
      const left = Math.max(8, rect.right + window.scrollX - menuWidth);
      setMenuStyle({
        position: 'absolute',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 9999,
      });
    }, [open]);

    const menu = (
      <div
        ref={menuRef}
        style={menuStyle}
        className="rounded-md bg-white shadow border w-48"
      >
        {actions?.map((act: any, i: number) => (
          <button
            key={i}
            onClick={(ev) => {
              ev.stopPropagation();
              setOpen(false);
              act.action(row.original);
            }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
          >
            {act.label}
          </button>
        ))}
      </div>
    );

    return (
      <div className="inline-block">
        <button
          ref={buttonRef}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((s) => !s);
          }}
          className="p-2 rounded hover:bg-gray-100"
        >
          <svg
            className="h-4 w-4 text-gray-600"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle cx="12" cy="5" r="1.5" fill="#6B7280" />
            <circle cx="12" cy="12" r="1.5" fill="#6B7280" />
            <circle cx="12" cy="19" r="1.5" fill="#6B7280" />
          </svg>
        </button>

        {open && createPortal(menu, document.body)}
      </div>
    );
  };

  const withLogo = [
    'Brand Name',
    'Category Name',
    'Kitchen Name',
    'Meal',
    'Variant',
    'Promotion Title',
    'Item name',
    'VENDOR',
    'PRODUCT NAME',
    'Product name',
    'Parent product',
    'Promotion title',
    'Vendor',
    'Vendors',
  ];
  const useDate = [
    'Date Created',
    'Date Updated',
    'Date & Time',
    'Start Date',
    'End Date',
    'Date',
    'DATE CREATED',
    'Date created',
    'Date updated',
  ];

  const openParam = {
    brands: 'slug',
    categories: 'slug',
    kitchens: 'id',
    meals: 'slug',
    orders: 'id',
    users: 'id',
    promotion: 'id',
    coupons: 'id',
    pilots: 'id',
    branch: 'id',
    foodItems: 'id',
    options: 'id',
    categoryRank: 'id',
    marketingCategories: 'name',
    cancelReasons: 'id',
    products: 'slug',
    stock: 'id',
    payments: 'id',
    companies: 'id',
    categoryRequests: 'id',
    scheduledPaymentRequests: 'id',
  };

  const editParam = {
    brands: 'slug',
    categories: 'slug',
    kitchens: 'id',
    meals: 'slug',
    orders: 'id',
    variants: 'slug',
    groups: 'slug',
    users: 'id',
    promotion: 'id',
    coupons: 'id',
    pilots: 'id',
    branch: 'id',
    foodItems: 'id',
    options: 'id',
    categoryRank: 'id',
    marketingCategories: 'name',
    deliveryRates: 'id',
    cancelReasons: 'id',
    products: 'slug',
    outlets: 'id',
    stock: 'id',
  };

  const formatData = (cell: any, row?: any) => {
    if (
      tableFor === 'payments' ||
      (tableFor === 'settlementBreakdown' && cell.column.id === 'status')
    ) {
      return cell.render('Cell');
    }

    if (useDate.includes(cell.column.Header))
      return cell.value
        ? moment(cell.value as MomentInput).format('MMM DD, YYYY')
        : '-';

    if (cell.column.Header === 'Status')
      return <StatusPill value={cell.value} />;

    if (cell.column.Header === 'Is Available?')
      return (
        <Toggle
          active={
            row.original.status === 'ACTIVE' || row.original.status === 'OPEN'
              ? true
              : false
          }
          module={tableFor}
          primaryKey={row.original.id}
          key={row.original.id}
          item={row.original}
        />
      );

    if (cell.column.Header === 'Item available?')
      return (
        <Toggle
          active={row.original.active}
          module={tableFor}
          primaryKey={row.original.id}
          key={row.original.id}
          item={row.original}
        />
      );

    if (cell.column.Header === 'Inactive' && isOwner)
      return (
        <CheckboxToggle
          active={row.original.status === 'INACTIVE' ? false : true}
          primaryKey={row.original.id}
          key={row.original.id}
          module={tableFor}
        />
      );

    if (cell.column.Header === 'Payment Status')
      return (
        <div className="flex items-center space-x-4 uppercase">
          <div
            className={`h-2 w-2 rounded-full mr-2 ${
              statusColors[
                cell.value === true
                  ? 'Paid'
                  : ('Pending' as keyof typeof statusColors)
              ]
            }`}
          />
          {cell.value === true ? 'Paid' : 'Pending'}
        </div>
      );

    if (cell.column.Header === 'Quick Actions')
      return (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            quickAction(e, row.original.id, tableFor);
          }}
        >
          <img className="h-6" src={quickActionIcon} alt="quick-action" />
        </button>
      );

    if (cell.column.Header === 'Price')
      return `₦${numeral(row.original.basePrice || row.original.price).format(
        '0,0'
      )}`;

    if (cell.column.Header === 'Option price')
      return `₦${numeral(getOptionPrice(row.original)).format('0,0')}`;

    if (cell.column.Header === 'Duplicate' && duplicateMethod)
      return (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            duplicateMethod(row.original.id);
          }}
        >
          <img className="h-6" src={duplicate} alt="duplicate" />
        </button>
      );

    if (cell.column.Header === 'Delete' && deleteMethod)
      return (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            deleteMethod(row.original.id);
          }}
        >
          <img className="h-6" src={deleteIcon} alt="delete" />
        </button>
      );

    if (cell.column.Header === 'Actions' && (cell.column as any).actions)
      return (
        <ActionDropdown actions={(cell.column as any).actions} row={row} />
      );

    if (cell.column.Header === 'Actions' && !((cell.column as any).actions)) {
      if (!openPage && !editMethod && !deleteMethod) {
        return cell.render('Cell');
      }
      return (
        <div className="flex items-center gap-3">
          {openPage && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openPage(
                  openParam[tableFor as keyof typeof openParam]
                    ? (row.original as any)[
                        openParam[tableFor as keyof typeof openParam]
                      ]
                    : row.original,
                  e
                );
              }}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              <SvgEye className="w-5 h-5" />
            </button>
          )}
          {editMethod && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                editMethod(
                  (row.original as any)[
                    editParam[tableFor as keyof typeof editParam]
                  ],
                  e
                );
              }}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              <svg width={18} height={18} viewBox="0 0 18 18" fill="none">
                <path
                  d="M13.5 1.5L16.5 4.5M2.25 15.75L2.93 13.2C2.96 13.08 2.98 13.02 3.01 12.96C3.03 12.91 3.06 12.86 3.09 12.82C3.13 12.77 3.18 12.72 3.28 12.63L11.48 4.93C11.59 4.83 11.64 4.78 11.7 4.76C11.75 4.74 11.81 4.74 11.87 4.76C11.92 4.78 11.97 4.83 12.08 4.93L12.92 5.7C13.02 5.79 13.07 5.84 13.09 5.89C13.11 5.94 13.11 5.99 13.09 6.04C13.07 6.09 13.02 6.14 12.92 6.23L4.72 13.93C4.63 14.02 4.58 14.06 4.53 14.09C4.48 14.12 4.43 14.14 4.38 14.15C4.32 14.17 4.26 14.18 4.13 14.2L2.25 15.75Z"
                  stroke="currentColor"
                  strokeWidth={1.2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          {deleteMethod && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                deleteMethod(row.original.id);
              }}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              <SvgTrash className="w-5 h-5" />
            </button>
          )}
        </div>
      );
    }

    return cell.render('Cell');
  };

  useEffect(() => {
    if (!setSelectedRows) return;
    const selectedObjects = selectedFlatRows.map((row) => row.original);
    setSelectedRows(selectedObjects);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowIds]);

  return (
    <div className={containerClassName || 'w-full'}>
      {!paginated && showSearch && (
        <div className="flex items-center space-x-3 justify-end mb-4">
          {selectedFlatRows.length > 0 && (
            <button
              onClick={() => {
                const idsToDelete = selectedFlatRows.map((row) => row.id);
                if (
                  typeof deleteMultiple === 'function' &&
                  window.confirm(`Delete ${idsToDelete.length} items?`)
                ) {
                  deleteMultiple(idsToDelete);
                }
              }}
              className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm flex items-center"
            >
              <img src={deleteIcon} className="h-4 mr-2 filter invert" alt="" />
              Delete Selected ({selectedFlatRows.length})
            </button>
          )}
          <input
            value={value || ''}
            onChange={(e) => {
              setValue(e.target.value);
              setGlobalFilter(e.target.value);
            }}
            placeholder="Search Table.."
            className="border border-slate-medium bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary rounded-lg"
          />
        </div>
      )}
      <div className="overflow-auto hide-scrollbar flex-1">
        <table
          {...getTableProps()}
          className="w-full border-collapse"
        >
          <thead className="sticky top-0 z-10">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => {
                  return (
                    <th
                      className={`text-start text-sm medium text-neutral-500 bg-slate-mild px-6 py-3 whitespace-nowrap ${theadClassName || ''}`}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      {column.render('Header')}
                      <span className="ml-1">
                        {column.isSorted
                          ? column.isSortedDesc
                            ? '↓'
                            : '↑'
                          : ''}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  className="cursor-pointer text-sm transition-colors hover:bg-gray-50 bg-white"
                  onClick={(e) =>
                    openPage
                      ? openPage(
                          openParam[tableFor as keyof typeof openParam]
                            ? (row.original as any)[
                                openParam[tableFor as keyof typeof openParam]
                              ]
                            : row.original,
                          e
                        )
                      : e.preventDefault()
                  }
                >
                  {row.cells.map((cell, index) => {
                    return cell.column.Header !== 'Action' &&
                      cell.column.Header !== 'Edit' ? (
                      <td
                        className="px-6 py-4 border-b border-neutral-200 align-middle whitespace-nowrap"
                        {...cell.getCellProps()}
                      >
                        <div className="flex items-center">
                          {withLogo.includes(cell.column.Header as string) && (() => {
                            const logoSrc =
                              (cell.column.Header === 'Vendor' || cell.column.Header === 'Vendors')
                                ? (row.original as any).logoUrl ||
                                  (row.original as any).brands?.[0]?.logoUrl
                                : (row.original as any).logoUrl ||
                                  (row.original as any).imageUrl;
                            return logoSrc ? (
                              <img
                                src={logoSrc}
                                className="hidden sm:block h-8 w-8 rounded-lg object-cover overflow-hidden bg-gray-100 mr-3 flex-shrink-0"
                                alt="logo"
                              />
                            ) : (
                              <span className="hidden sm:block h-8 w-8 rounded-lg bg-gray-100 mr-3 flex-shrink-0" />
                            );
                          })()}
                          {formatData(cell, row)}
                        </div>
                      </td>
                    ) : (
                      <td
                        className="px-6 py-4 border-b border-neutral-200 align-middle"
                        key={cell.value || Math.random() + i}
                      >
                        <div className="flex items-center space-x-4">
                          {rowActions && rowActions.length > 0 ? (
                            <ActionDropdown actions={rowActions} row={row} />
                          ) : (
                            <button
                              onClick={(e) =>
                                editMethod(
                                  (row.original as any)[
                                    editParam[tableFor as keyof typeof editParam]
                                  ],
                                  e
                                )
                              }
                            >
                              <img src={edit} alt="edit" />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
